import { SectionError, SectionLoading } from '@/components/section-state';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { BarChart3 } from 'lucide-react';
import { useState } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import ClinicUsageUserTable from './ClinicUsageUserTable';
import type { TrendRange } from './types';
import { useClinicUsageMetrics } from './useClinicUsageQueries';

const trendChartConfig = {
  sessions: { label: 'Lotur', color: 'var(--primary)' },
  active_users: { label: 'Virkir notendur', color: 'hsl(var(--muted-foreground))' },
};

const RANGE_OPTIONS: { value: TrendRange; label: string }[] = [
  { value: '7d', label: '7 dagar' },
  { value: '30d', label: '30 dagar' },
  { value: '90d', label: '90 dagar' },
];

// Window label for the cards / table headings, matching the selected range.
const WINDOW_LABEL: Record<TrendRange, string> = {
  '7d': 'síðustu 7 dagar',
  '30d': 'síðustu 30 dagar',
  '90d': 'síðustu 90 dagar',
};

const Stat = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded-lg border p-3">
    <div className="font-semibold text-2xl">{value}</div>
    <div className="text-muted-foreground text-xs">{label}</div>
  </div>
);

const RangeToggle = ({ range, setRange }: { range: TrendRange; setRange: (range: TrendRange) => void }) => (
  <div className="flex gap-1">
    {RANGE_OPTIONS.map((option) => (
      <Button
        key={option.value}
        type="button"
        size="sm"
        variant={range === option.value ? 'default' : 'outline'}
        className={cn('h-7 px-2 text-xs')}
        onClick={() => setRange(option.value)}
      >
        {option.label}
      </Button>
    ))}
  </div>
);

const ClinicUsageSection = () => {
  const { authOrgId } = useAuth();
  const [range, setRange] = useState<TrendRange>('30d');
  const metricsQ = useClinicUsageMetrics(authOrgId, range);
  const windowLabel = WINDOW_LABEL[range];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            <CardTitle>Notkun</CardTitle>
          </div>
          <RangeToggle range={range} setRange={setRange} />
        </div>
        <CardDescription>Virkir notendur og lotur fyrir stöðina þína.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {metricsQ.isLoading ? (
          <SectionLoading label="Sæki notkun" />
        ) : metricsQ.isError || !metricsQ.data ? (
          <SectionError title="Notkun hlóðst ekki" error={metricsQ.error} onRetry={() => metricsQ.refetch()} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Stat label={`Virkir notendur (${windowLabel})`} value={metricsQ.data.totals.active_users} />
              <Stat label={`Lotur (${windowLabel})`} value={metricsQ.data.totals.sessions} />
            </div>

            <div className="space-y-3">
              <h3 className="font-medium text-sm">Þróun</h3>
              {metricsQ.data.buckets.length === 0 ? (
                <div className="flex h-[240px] items-center justify-center rounded-md border border-dashed text-muted-foreground text-sm">
                  Engin gögn fundust fyrir valið tímabil.
                </div>
              ) : (
                <ChartContainer config={trendChartConfig} className="h-[240px] w-full">
                  <LineChart data={metricsQ.data.buckets} margin={{ left: 8, right: 16, top: 8 }}>
                    <CartesianGrid vertical={false} />
                    <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} />
                    <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} width={32} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line
                      type="monotone"
                      dataKey="sessions"
                      stroke="var(--color-sessions)"
                      dot={false}
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="active_users"
                      stroke="var(--color-active_users)"
                      dot={false}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ChartContainer>
              )}
            </div>

            <div>
              <h3 className="mb-2 font-medium text-sm">Notkun eftir notanda ({windowLabel})</h3>
              <ClinicUsageUserTable users={metricsQ.data.users} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ClinicUsageSection;
